import React from 'react';
import { ColorPicker, Switch } from 'antd';
import { C } from '../../constants/design-tokens';
import { ChromaKeyParams, DEFAULT_CHROMA_KEY_PARAMS } from '../constants/chroma-key-shader';
import { SliderRow, buttonStyle, labelStyle, rowStyle, valueStyle } from './shared';

type ChromaKeyControlsProps = {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  params: ChromaKeyParams;
  onParamsChange: (params: ChromaKeyParams) => void;
  onCommit?: (enabled: boolean, params: ChromaKeyParams) => void;
};

function hexToNormalizedRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return [r, g, b];
}

function normalizedRgbToHex(rgb: [number, number, number]): string {
  const toHex = (value: number) =>
    Math.round(value * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(rgb[0])}${toHex(rgb[1])}${toHex(rgb[2])}`;
}

export const ChromaKeyControls: React.FC<ChromaKeyControlsProps> = ({
  enabled,
  onToggle,
  params,
  onParamsChange,
  onCommit
}) => {
  const hexColor = normalizedRgbToHex(params.keyColor);

  const previewColor = (hex: string) => onParamsChange({ ...params, keyColor: hexToNormalizedRgb(hex) });

  const commitColor = (hex: string) => {
    const next = { ...params, keyColor: hexToNormalizedRgb(hex) };
    onParamsChange(next);
    onCommit?.(enabled, next);
  };

  const commitParams = () => onCommit?.(enabled, params);

  const reset = () => {
    onParamsChange(DEFAULT_CHROMA_KEY_PARAMS);
    onCommit?.(enabled, DEFAULT_CHROMA_KEY_PARAMS);
  };

  return (
    <div style={{ padding: '6px 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={rowStyle}>
        <span style={{ ...labelStyle, color: C.textPrimary }}>Habilitar</span>
        <Switch
          size='small'
          checked={enabled}
          onChange={next => {
            onToggle(next);
            onCommit?.(next, params);
          }}
        />
      </div>

      {enabled && (
        <>
          <div>
            <div style={rowStyle}>
              <span style={labelStyle}>Color clave</span>
              <span style={valueStyle}>{hexColor.toUpperCase()}</span>
            </div>
            <div style={{ marginTop: 6 }}>
              <ColorPicker
                value={hexColor}
                onChange={color => previewColor(color.toHexString())}
                onChangeComplete={color => commitColor(color.toHexString())}
                showText
                size='small'
              />
            </div>
          </div>

          <SliderRow
            label='similarity'
            value={params.similarity}
            min={0.01}
            max={0.6}
            step={0.01}
            onChange={similarity => onParamsChange({ ...params, similarity })}
            onCommit={commitParams}
          />
          <SliderRow
            label='smoothness'
            value={params.smoothness}
            min={0.01}
            max={0.5}
            step={0.01}
            onChange={smoothness => onParamsChange({ ...params, smoothness })}
            onCommit={commitParams}
          />
          <SliderRow
            label='spill'
            value={params.spill}
            min={0.01}
            max={0.5}
            step={0.01}
            onChange={spill => onParamsChange({ ...params, spill })}
            onCommit={commitParams}
          />

          <button onClick={reset} style={{ ...buttonStyle, alignSelf: 'flex-start' }}>
            Reset valores
          </button>
        </>
      )}
    </div>
  );
};
